import logger from "../utils/logger";
import moment from "moment";
import { AdjustRateParams, InvoiceRequestParams } from "../model";
import { getEffortsDetails, getWorkingDaysInMonth, getWorkingDaysPerMonth } from "../pages/report/payments/efforts";
import { parseClassNames } from "@fullcalendar/core/internal";
import { COMMERCIAL_PARTNER_IKAT, COMMERCIAL_PARTNER_RT_EXCEPTION, DEDUCTION_TYPE_DOLLARS, IKAT_DEDUCTION_PERCENTAGE_DEFAULT, RT_DEDUCTION_PERCENTAGE_DEFAULT } from "../constants/common";

// Per-project effective (billed) hourly rate for the IKAT / RT statements.
//   'dollars'    -> rate - deduction  (never below 0)
//   otherwise    -> rate x pct / 100  where pct falls back to the legacy %
//                  (IKAT 75, RT 100) when the mapping has no configured value.
const effRateExpr = (rateCol: string, typeCol: string, valueCol: string, defaultPct: number) => {
    const type = `LOWER(COALESCE(${typeCol}, ''))`;
    return `CASE WHEN ${type} = '${DEDUCTION_TYPE_DOLLARS}'
            THEN GREATEST(0, COALESCE(${rateCol}, 0) - COALESCE(${valueCol}, 0))
            ELSE GREATEST(0, COALESCE(${rateCol}, 0) * COALESCE(${valueCol}, ${defaultPct}) / 100)
        END`;
};

// Fetch project details for validation
export async function fetchProjectDetails(params: { uuid: string; pid: string; rate: string | number }) {
    let _q = `SELECT * FROM project WHERE uuid = '${params.uuid}' AND pid = '${params.pid}' AND rate = '${params.rate}'`;
    try {
        console.log("fetchProjectDetails query:", _q);
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["project"],
            }),
        });
        const json = await response.json();
        if (!response.ok) {
            console.error("fetchProjectDetails response error:", response.status, json);
        }
        return json;
    } catch (error) {
        logger.error("fetchProjectDetails failed", {}, error);
        return [];
    }
}

// Reset invoices for a specific uuid, pid, rate, and date range (for rate change scenario)
// Reset invoices for a specific uuid, pid, and date range (for rate change scenario)
export async function resetInvoiceOnRateChange(params: { uuid: string; pid: string; start_dt: string; end_dt: string }) {
    let _q = `UPDATE finance SET 
        payment = NULL,
        verified = NULL,
        created_by = '',
        updated_by = '',
        _ts_bill = NULL,
        _ts_payment = NULL,
        _ts_verify = NULL,
        invoice_no = NULL,
        _ts_invoice = NULL,
        _ts_invoice_due = NULL
        WHERE uuid = '${params.uuid}' AND pid = '${params.pid}' AND _d BETWEEN '${params.start_dt}' AND '${params.end_dt}'`;

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("resetInvoiceOnRateChange failed", {}, error);
        return [];
    }
}

export async function getFinanceAgregatedDetails(filters: any) {
    //FIXME
    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: "select _d, count(_d), f.hrs * p.rate as bill  , sum(payment), sum(verified), sum(hrs) from finance as f left join project as p on f.uuid = p.uuid and p.active = true group by _d",
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("getFinanceAgregatedDetails failed", {}, error);
        return [];
    }
}
export async function getFinanceDetails(filters: any) {
    //FIXME - Date, User, Project

    let _q = "select *, p.rate * f.hrs as bill from finance as f left join consultant as c on f.uuid = c.uuid left join project as p on p.uuid = c.uuid and '" + filters.date + "' between p.start_dt and p.end_dt  where 1=1 ";
    if (filters.date) {
        _q += "and _d = " + "'" + filters.date + "'";
    }

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("getFinanceDetails failed", {}, error);
        return [];
    }
}
export async function getFinanceReportData(filters: any) {
    // console.log(filters);
    try {
        let _q =
            "SELECT  SUM(IFNULL(finance.payment, 0)) as payment, month(_d) as m, _d, MONTHNAME(_d)  as month1, YEAR(_d) AS year, DATE_FORMAT(_d, '%Y-%m') AS month, sum(project.rate * finance.hrs) as bill, sum(hrs) as hrs, invoice_no, (ROUND(COALESCE(sum(project.rate * finance.hrs),0),2)-ROUND(COALESCE(sum(finance.payment),0),2)) as diff, (COALESCE((fixed_payroll),0)-COALESCE(sum(finance.payment),0)) as fixed_diff, finance.uuid ,project_master.name,consultant.name,project.rate as prate, project.rate as rate from finance LEFT JOIN consultant ON consultant.uuid =finance.uuid LEFT JOIN project ON consultant.uuid = project.uuid  AND _d BETWEEN project.start_dt AND project.end_dt LEFT JOIN project_master ON project.pid = project_master.pid  ";
        _q += " where 1=1 ";
        if (filters && filters.checkDate) {
            _q += "  and month(_d) = " + (filters.checkDate.getMonth() + 1) + "";
            _q += "  and year(_d) = " + filters.checkDate.getFullYear() + "";
        }

        if (filters && filters.employee) {
            _q += "  and finance.uuid = '" + filters.employee + "' ";
        }

        _q += " group by uuid, consultant.name, month1, year ";

        _q += " order by consultant.name asc, _d asc ";

        console.log(_q);

        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("getFinanceReportData failed", {}, error);
        return [];
    }
}

export async function getDuplicateInvoiceNumber() {
    console.log("Fetching duplicate invoice numbers...");
    const _q = `
       SELECT 
            invoice_no, 
            MONTH(_d) as month, 
            YEAR(_d) as year, 
            GROUP_CONCAT(DISTINCT uuid) as uuids,
            COUNT(DISTINCT uuid) as uuid_count
        FROM finance
        WHERE invoice_no IS NOT NULL AND invoice_no <> ''
        GROUP BY invoice_no
        HAVING uuid_count > 1
        ORDER BY year DESC, month DESC, invoice_no
    `;

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("getDuplicateInvoiceNumber failed", {}, error);
        return [];
    }
}

export async function getFinanceReportDataByIkat(filters: any) {
    // console.log(filters);
    try {
        const rate = effRateExpr("project.ikat_rate", "project.ikat_deduction_type", "project.ikat_deduction_value", IKAT_DEDUCTION_PERCENTAGE_DEFAULT);
        const bill = `sum(${rate} * finance.hrs)`;
        const paidBill = `CASE WHEN SUM(IFNULL(finance.payment, 0)) > 0 THEN ${bill} ELSE 0 END`;

        let _q = `SELECT ${paidBill} as payment, month(_d) as m, _d, project_master.name AS pid, MONTHNAME(_d)  as month1, YEAR(_d) AS year, DATE_FORMAT(_d, '%Y-%m') AS month, _ts_bill, CASE WHEN MAX(invoice_no) IS NOT NULL AND MAX(invoice_no) <> '' THEN DATE_FORMAT(MAX(_ts_invoice), '%Y-%m') ELSE NULL END AS billMonth, _ts_invoice_due, CASE WHEN MAX(invoice_no) IS NOT NULL AND MAX(invoice_no) <> '' THEN DATE_FORMAT(MAX(_ts_invoice_due), '%Y-%m') ELSE NULL END AS invoiceDueMonth, ${bill} as bill, sum(hrs) as hrs, MAX(invoice_no) as invoice_no, (COALESCE(${bill},0) - ${paidBill}) as diff, (COALESCE((fixed_payroll),0) - ${paidBill}) as fixed_diff, (sum(hrs) - CASE WHEN SUM(IFNULL(finance.payment, 0)) > 0 THEN ${bill} / NULLIF(COALESCE(project.ikat_rate, 0), 0) ELSE 0 END) as balance_hrs, finance.uuid ,project_master.name,consultant.name,project.ikat_rate as prate, ${rate} as rate from finance LEFT JOIN consultant ON consultant.uuid =finance.uuid LEFT JOIN project ON consultant.uuid = project.uuid AND _d BETWEEN project.start_dt AND project.end_dt  LEFT JOIN project_master ON project.pid = project_master.pid `;
        _q += " where 1=1 and project.ikat_rate IS NOT NULL and project.ikat_rate > 0 ";
        if (filters && filters.checkDate) {
            _q += "  and month(_d) = " + (filters.checkDate.getMonth() + 1) + "";
            _q += "  and year(_d) = " + filters.checkDate.getFullYear() + "";
        }

        if (filters && filters.employee) {
            _q += "  and finance.uuid = '" + filters.employee + "' ";
        }

        _q += " group by uuid, consultant.name,  project_master.pid, month1, year ";

        _q += " order by consultant.name asc ";

        console.log(_q);

        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        const rows = await response.json();

        // Post-process to handle NaN and -0 for diff.
        // A failed query returns {"error": ...} instead of a list; never hand
        // that to a .map() in the UI.
        if (!Array.isArray(rows)) {
            return [];
        }

        const processedRows = rows.map((row) => {
            // If IKAT rate is zero, set bill and payment to 0
            if (Number(row.rate) === 0 || row.rate === null || row.rate === undefined) {
                row.bill = 0;
                row.payment = 0;
                row.diff = 0;
            }
            // Recalculate bill and payment from the 2-decimal rounded rate so they match the displayed rate
            const displayRate = Number(Number(row.rate).toFixed(2));
            const calcBill = Number((displayRate * row.hrs).toFixed(2));
            row.bill = calcBill;
            row.payment = Number(row.payment) > 0 ? calcBill : 0;
            // Recalculate diff as bill - payment
            row.diff = Number(row.bill) - Number(row.payment);
            // If balance is zero, set balance_hrs to zero as well
            if (row.diff === 0) {
                row.balance_hrs = 0;
            }
            // Optionally, also handle diff_wo_deductions if present
            if (row.diff_wo_deductions !== undefined) {
                row.diff_wo_deductions = Number(row.diff_wo_deductions);
                if (isNaN(row.diff_wo_deductions) || Object.is(row.diff_wo_deductions, -0) || row.diff_wo_deductions === null || Math.abs(row.diff_wo_deductions) < 0.01) {
                    row.diff_wo_deductions = 0;
                } else {
                    row.diff_wo_deductions = Number(row.diff_wo_deductions.toFixed(2));
                }
            }
            return row;
        });

        return processedRows;
    } catch (error) {
        logger.error("getFinanceReportDataByIkat failed", {}, error);
        return [];
    }
}

// RT mirrors the IKAT statement, but bills on the RT commercial partner's rate:
//   - commercial_partner = 'RT'           -> bill = rt_rate * hrs
//   - commercial_partner = 'RT exception' -> bill = project.rate * hrs (no RT rate applies)
// The Payments tab is unaffected and keeps using the normal project rate.
export async function getFinanceReportDataByRT(filters: any) {
    try {
        const isRtException = `LOWER(COALESCE(project.commercial_partner, '')) = '${COMMERCIAL_PARTNER_RT_EXCEPTION.toLowerCase()}'`;
        const effRate = `CASE WHEN ${isRtException} THEN COALESCE(project.rate, 0) ELSE ${effRateExpr("project.rt_rate", "project.rt_deduction_type", "project.rt_deduction_value", RT_DEDUCTION_PERCENTAGE_DEFAULT)} END`;
        const rate = `${effRate}`;
        const bill = `sum(${rate} * finance.hrs)`;
        const paidBill = `CASE WHEN SUM(IFNULL(finance.payment, 0)) > 0 THEN ${bill} ELSE 0 END`;

        let _q = `SELECT ${paidBill} as payment, month(_d) as m, _d, project_master.name AS pid, MONTHNAME(_d) as month1, YEAR(_d) AS year, DATE_FORMAT(_d, '%Y-%m') AS month, _ts_bill, CASE WHEN MAX(invoice_no) IS NOT NULL AND MAX(invoice_no) <> '' THEN DATE_FORMAT(MAX(_ts_invoice), '%Y-%m') ELSE NULL END AS billMonth, _ts_invoice_due, CASE WHEN MAX(invoice_no) IS NOT NULL AND MAX(invoice_no) <> '' THEN DATE_FORMAT(MAX(_ts_invoice_due), '%Y-%m') ELSE NULL END AS invoiceDueMonth, ${bill} as bill, sum(hrs) as hrs, MAX(invoice_no) as invoice_no, (COALESCE(${bill},0) - ${paidBill}) as diff, (COALESCE((fixed_payroll),0) - ${paidBill}) as fixed_diff, (sum(hrs) - CASE WHEN SUM(IFNULL(finance.payment, 0)) > 0 THEN ${bill} / NULLIF(${rate}, 0) ELSE 0 END) as balance_hrs, finance.uuid, project_master.name, consultant.name, project.rt_rate as prate, ${rate} as rate from finance LEFT JOIN consultant ON consultant.uuid = finance.uuid LEFT JOIN project ON consultant.uuid = project.uuid AND _d BETWEEN project.start_dt AND project.end_dt LEFT JOIN project_master ON project.pid = project_master.pid `;
        // RT scope: an explicit RT mapping, or any untagged mapping that carries an
        // RT rate (so nothing silently disappears). Mappings explicitly marked IKAT
        // are always excluded, which keeps this report separate from the IKAT one.
        const notIkat = `LOWER(COALESCE(project.commercial_partner, '')) <> '${COMMERCIAL_PARTNER_IKAT.toLowerCase()}'`;
        _q += " where 1=1 and ((project.rt_rate IS NOT NULL and project.rt_rate > 0 and " + notIkat + ") or (" + isRtException + " and project.rate IS NOT NULL and project.rate > 0)) ";
        if (filters && filters.checkDate) {
            _q += "  and month(_d) = " + (filters.checkDate.getMonth() + 1) + "";
            _q += "  and year(_d) = " + filters.checkDate.getFullYear() + "";
        }

        if (filters && filters.employee) {
            _q += "  and finance.uuid = '" + filters.employee + "' ";
        }

        _q += " group by uuid, consultant.name, project_master.pid, month1, year ";
        _q += " order by consultant.name asc ";

        console.log(_q);

        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        const rows = await response.json();

        // Same post-processing as the IKAT statement, so both tables agree
        // with the rate they display. A failed query returns {"error": ...};
        // never hand that to a .map() in the UI.
        if (!Array.isArray(rows)) {
            return [];
        }

        const processedRows = rows.map((row) => {
            // If the RT rate is zero, set bill and payment to 0
            if (Number(row.rate) === 0 || row.rate === null || row.rate === undefined) {
                row.bill = 0;
                row.payment = 0;
                row.diff = 0;
            }
            // Recalculate bill and payment from the 2-decimal rounded rate so they match the displayed rate
            const displayRate = Number(Number(row.rate).toFixed(2));
            const calcBill = Number((displayRate * row.hrs).toFixed(2));
            row.bill = calcBill;
            row.payment = Number(row.payment) > 0 ? calcBill : 0;
            // Recalculate diff as bill - payment
            row.diff = Number(row.bill) - Number(row.payment);
            // If balance is zero, set balance_hrs to zero as well
            if (row.diff === 0) {
                row.balance_hrs = 0;
            }
            return row;
        });

        return processedRows;
    } catch (error) {
        logger.error("getFinanceReportDataByRT failed", {}, error);
        return [];
    }
}

export async function getInvoicesData(filters: any) {
    console.log(filters);

    try {
        // When a project has Over Time applicable, each day's regular hours are capped at 8 for the
        // base calculations; the hours above 8 are summed separately as overtime_hours and billed
        // at the overtime rate.
        const regular_hrs = "CASE WHEN COALESCE(p.over_time, 0) = 1 THEN LEAST(IFNULL(f.hrs, 0), 8) ELSE IFNULL(f.hrs, 0) END";
        let _q = `
    SELECT 
        f.uuid, 
        f._d as _d, 
        f._ts_invoice, 
        f._ts_invoice_due,  
        c.name, 
        f.pid as pid,
        COALESCE(pm.name, pm_fallback.name, pm_main.name) AS project_name, 
        DATE_FORMAT(f._d, '%Y-%m') AS month, 
        month(f._d) as m, 
        MONTHNAME(f._d) AS month1, 
        YEAR(f._d) AS year, 
        CASE WHEN p.billing_period = 'Weekly' THEN WEEK(f._d, 1) ELSE NULL END AS calendar_week, 
        CASE 
            WHEN p.billing_period = 'Customized' THEN 'Customized'
            WHEN p.billing_period = 'Weekly' THEN 'Weekly' 
            ELSE 'Monthly' 
        END AS billing_group, 

        ROUND( SUM(${regular_hrs}) * COALESCE(NULLIF(MAX(p.rate), 0), MAX(p_main.rate)), 2) AS bill_wo_deductions,


    ROUND( ROUND( SUM(${regular_hrs}) * COALESCE(NULLIF(MAX(p.rate), 0), MAX(p_main.rate)), 2) * (1 - (COALESCE(p.insurance_fee,0) + COALESCE(p.vmo,0)) / 100), 2) AS bill,

    SUM(${regular_hrs}) AS hrs,
    COALESCE(SUM(GREATEST(0, f.hrs - 8)), 0) AS overtime_hours,
    COALESCE(MAX(p.over_time), 0) AS over_time,
    MAX(p.over_time_rate) AS over_time_rate,
    SUM(f.payment) AS payment,

    COUNT(DISTINCT f._d) AS _punched,
        
    COUNT(DISTINCT f.invoice_no) AS _invoiced,

        c.fixed_payroll,

        p.fixed_payment,

        CASE
    WHEN ABS( ROUND(COALESCE(SUM(p.rate * ${regular_hrs}), 0), 2) - ROUND(IFNULL(f.payment, 0), 2) ) <= 0.05 THEN 0.00
    ELSE ROUND( ROUND(COALESCE(SUM(p.rate * ${regular_hrs}), 0), 2) - ROUND(IFNULL(f.payment, 0), 2), 2)
        END AS diff_wo_deductions,

        CASE
        WHEN ABS(
            ROUND( ROUND(COALESCE(SUM(p.rate * ${regular_hrs}),0), 2) * (1 - (COALESCE(p.insurance_fee,0) + COALESCE(p.vmo,0)) / 100), 2)
            - ROUND(IFNULL(f.payment,0), 2)
        ) <= 0.05 THEN 0.00
        ELSE ROUND(
            ROUND( ROUND(COALESCE(SUM(p.rate * ${regular_hrs}),0), 2) * (1 - (COALESCE(p.insurance_fee,0) + COALESCE(p.vmo,0)) / 100), 2)
            - ROUND(IFNULL(f.payment,0), 2)
        , 2)
        END AS diff,

        ROUND( ROUND(COALESCE(p.fixed_payment, 0), 2) - SUM(ROUND(IFNULL(f.payment, 0), 2)), 2) AS fixed_diff,
        
    GROUP_CONCAT(DISTINCT p.billing_period) AS billing_period, 
    GROUP_CONCAT(DISTINCT f.invoice_no) AS invoice_no, 
    COALESCE(NULLIF(MAX(p.rate), 0), MAX(p_main.rate)) AS prate,
    f.updated_by,
    COALESCE(pm.name, pm_fallback.name, pm_main.name) as project_name,
    p.pid as project_pid,
    p.start_dt as project_start_dt,
    p.end_dt as project_end_dt,

        CASE 
            WHEN p.billing_period = 'Customized' THEN MIN(pp.period_st_dt)
            WHEN p.billing_period = 'Weekly' THEN DATE_SUB(MIN(f._d), INTERVAL WEEKDAY(MIN(f._d)) DAY)
            ELSE DATE_FORMAT(MIN(f._d), '%Y-%m-01')
        END AS period_start,
        CASE 
            WHEN p.billing_period = 'Customized' THEN MIN(pp.period_end_dt)
            WHEN p.billing_period = 'Weekly' THEN DATE_ADD(DATE_SUB(MIN(f._d), INTERVAL WEEKDAY(MIN(f._d)) DAY), INTERVAL 6 DAY)
            ELSE LAST_DAY(MIN(f._d))
        END AS period_end, 

        COALESCE(pm.cid, pm_fallback.cid) as cid,

        p.insurance_fee as insurance_fee,
        p.vmo as vmo,

        COALESCE(DATE_FORMAT(MIN(pp.record_st_dt), '%Y-%m-%d'), DATE_FORMAT(MIN(f._d), '%Y-%m-01')) AS record_st_dt,
        COALESCE(DATE_FORMAT(MIN(pp.record_end_dt), '%Y-%m-%d'), DATE_FORMAT(LAST_DAY(MIN(f._d)), '%Y-%m-%d')) AS record_end_dt


    FROM (
        SELECT 
            uuid, 
            pid, 
            _d, 
            SUM(hrs) as hrs, 
            SUM(payment) as payment, 
            MAX(invoice_no) as invoice_no, 
            MAX(_ts_invoice) as _ts_invoice, 
            MAX(_ts_invoice_due) as _ts_invoice_due, 
            MAX(updated_by) as updated_by,
            count(DISTINCT finance._d) as _punched,
            CASE WHEN invoice_no IS NOT NULL AND invoice_no <> '' THEN 1 ELSE 0 END as _invoiced
        FROM finance
        WHERE hrs > 0
        GROUP BY uuid, pid, _d
    ) f
    LEFT JOIN consultant c ON c.uuid = f.uuid
    LEFT JOIN project p ON p.pid = f.pid AND c.uuid = p.uuid AND f._d BETWEEN p.start_dt AND p.end_dt
    LEFT JOIN project_master pm ON p.pid = pm.pid
    LEFT JOIN project_master pm_fallback ON f.pid = pm_fallback.pid
    LEFT JOIN (
        SELECT p2.uuid, p2.pid, p2.rate, p2.start_dt, p2.end_dt
        FROM project p2
        WHERE p2.rate IS NOT NULL AND p2.rate > 0
    ) p_main ON p_main.uuid = c.uuid AND f._d BETWEEN p_main.start_dt AND p_main.end_dt
    LEFT JOIN project_master pm_main ON p_main.pid = pm_main.pid
    LEFT JOIN project_period pp 
                ON f.uuid = pp.uuid 
                AND f.pid = pp.pid 
                AND f._d BETWEEN pp.record_st_dt AND pp.record_end_dt
                AND (p.billing_period = 'Monthly' OR pp.id = (
                        SELECT pp2.id
                        FROM project_period pp2
                        WHERE pp2.uuid = f.uuid
                            AND pp2.pid = f.pid
                            AND f._d BETWEEN pp2.record_st_dt AND pp2.record_end_dt
                        ORDER BY pp2.record_st_dt DESC, pp2.id ASC
                        LIMIT 1
                ))
    WHERE 1=1
    ${filters?.employeeStatus !== undefined && filters?.employeeStatus !== "all" ? ` AND c.active = ${filters?.employeeStatus !== "inactive" ? 1 : 0}` : ""}
    ${filters?.checkDate ? ` AND MONTH(f._d) = ${filters?.checkDate.getMonth() + 1} AND YEAR(f._d) = ${filters?.checkDate.getFullYear()}` : ""}
    ${filters?.employee ? ` AND f.uuid = '${filters?.employee}'` : ""}
    ${filters?.yearLimit != undefined && filters?.yearLimit !== null ? ` AND YEAR(f._d) >= ${filters.yearLimit ? 2025 : 2010}` : ""}
    ${filters?.billingPeriod && filters.billingPeriod !== "all" ? ` AND p.billing_period = '${filters.billingPeriod}'` : ""}
    ${filters?.clientId && filters.clientId !== "all" ? ` AND COALESCE(pm.cid, pm_fallback.cid) = '${filters.clientId}'` : ""}
    GROUP BY 
        f.uuid, 
        c.name, 
        p.pid,
        pm.name,
        pm_fallback.name,
        pm.cid,
        pm_fallback.cid,
        YEAR(f._d),
        p.rate,
        CASE 
            WHEN p.billing_period = 'Customized' THEN 1
            WHEN p.billing_period = 'Weekly' THEN 2
            ELSE 3
        END,
        CASE 
            WHEN p.billing_period = 'Customized' THEN pp.record_st_dt
            WHEN p.billing_period = 'Weekly' THEN YEARWEEK(f._d, 1)
            ELSE MONTH(f._d)
        END,
        CASE 
            WHEN p.billing_period = 'Customized' THEN pp.record_end_dt
            WHEN p.billing_period = 'Weekly' THEN NULL
            ELSE YEAR(f._d)
        END
    ORDER BY c.name ASC, period_start ASC, f.uuid asc, pm.name asc
    `;

        console.log(_q);

        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        // return await response.json();

        const rows = await response.json(); // your DB fetch
        console.log("Rows fetched:", rows);

        // Post-process rows
        const processedRows = await Promise.all(
            rows?.map(async (row) => {
                const year = moment(row._d).year();
                const month = moment(row._d).month() + 1; // moment: 0-based
                const workingDays = await getWorkingDaysInMonth(year, month);
                row.workingDays = workingDays;

                // Apply Deductions if not on fixed payroll
                if (row.insurance_fee || row.vmo) {
                    row.insurance_fee = Number(row.insurance_fee);
                    row.vmo = Number(row.vmo);
                    row.deductions = (Number(row.bill_wo_deductions) * (Number(row.insurance_fee) + Number(row.vmo))) / 100;
                    row.billPerDay = (Number(row.bill_wo_deductions - row.deductions) / Number(row._punched)).toFixed(2);
                } else {
                    row.billPerDay = null;
                }

                // Fixed payroll logic
                if (row.fixed_payroll && row.fixed_payroll > 0) {
                    try {
                        const effortsDetails = await getEffortsDetails(row);
                        if (effortsDetails) {
                            row.effortsDetails = effortsDetails;
                            row.workingDays = effortsDetails && effortsDetails[0]?.working_days;
                            row.sales_tax = effortsDetails[0]?.sales_tax;
                        }
                    } catch (error) {
                        logger.error("getInvoicesData failed", {}, error);
                    }

                    row.fixed_payroll = Number(row.fixed_payroll);
                    row.fixed_payment = Number(row.fixed_payment);

                    row.bill = (Number(row.fixed_payment) / Number(row.workingDays)) * Number(row._punched);
                    row.diff = (Number(row.fixed_diff) / Number(row.workingDays)) * Number(row._punched);
                    row.billPerDay = Number(row.fixed_payment) / Number(row.workingDays);

                    if (row.payment > 0) {
                        row.diff = 0;
                        // The recorded payment also covers sales tax (see performPaymentAPI), so the
                        // amount received must mirror the displayed Bill (base + sales tax); otherwise
                        // the Balance column keeps showing the sales tax as outstanding after paying.
                        const salesTaxAmount = (Number(row.bill) * (Number(row.sales_tax) || 0)) / 100;
                        row.payment = Number(row.bill) + salesTaxAmount;
                    }

                    // Round fixed payroll results
                    row.payment = Number(row?.payment?.toFixed(2));
                    row.bill = Number(row?.bill?.toFixed(2));
                    row.diff = Number(row?.diff?.toFixed(2));
                }
                // For non-fixed payroll: SQL already rounded, keep as-is
                // Only ensure they are Numbers, not strings
                else {
                    row.payment = Number(row.payment);
                    row.bill = Number(row.bill);
                    row.diff = Number(row.diff);
                    row.bill_wo_deductions = Number(row.bill_wo_deductions);
                    row.diff_wo_deductions = Number(row.diff_wo_deductions);

                    if (row.payment > 0) {
                        row.diff = 0;
                        row.payment = row.bill;
                    }
                }

                // Add after the fixed payroll block, for all records:
                if (Math.abs(Number(row.diff)) <= 0.05) {
                    row.diff = 0;
                }
                if (Math.abs(Number(row.diff_wo_deductions)) <= 0.05) {
                    row.diff_wo_deductions = 0;
                }

                // Overtime: hours punched beyond 8/day x overtime rate when applicable
                row.overtime_hours = Number(row.overtime_hours) || 0;
                row.over_time = Number(row.over_time) || 0;
                row.over_time_rate = Number(row.over_time_rate) || 0;
                row.over_time_amount = row.over_time > 0 ? Number((row.overtime_hours * row.over_time_rate).toFixed(2)) : 0;

                return row;
            }),
        );

        return processedRows;
    } catch (error) {
        logger.error("getInvoicesData failed", {}, error);
        return [];
    }
}

export async function getFinanceReportDataByMonth(filters: any) {
    // console.log(filters);
    try {
        let _q = `
            SELECT  SUM(IFNULL(finance.payment, 0)) as payment, 
            
            MONTHNAME(_d)  as month1, 
            
            YEAR(_d) AS year, 
            
            DATE_FORMAT(_d, '%Y-%m') AS month,  
            
            sum(project.rate * finance.hrs) as bill, 

            count(DISTINCT finance._d) as _punched,
            
            sum(hrs) as hrs,
            
            (COALESCE(sum( project.rate * finance.hrs),0)-COALESCE(sum(finance.payment),0)) as diff, 

            (COALESCE((fixed_payroll),0)-COALESCE(sum(finance.payment),0)) as fixed_diff, 

            consultant.fixed_payroll,

            project.fixed_payment,
            
            project_master.name,
                        
            project.rate 
            
            from finance 
            
            LEFT JOIN consultant ON consultant.uuid =finance.uuid 
            
            LEFT JOIN project ON finance.pid = project.pid AND finance._d BETWEEN project.start_dt AND project.end_dt 
            
            LEFT JOIN project_master ON project.pid = project_master.pid
            `;
        _q += " where 1=1 AND hrs > 0 ";

        if (filters && filters.checkDate) {
            _q += "  and year(_d) = " + filters.checkDate.getFullYear() + "";
        }

        _q += " group by month1, year order by _d";

        // console.log(_q);

        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        // return await response.json();

        const rows = await response.json(); // your DB fetch
        console.log("Rows fetched:", rows);

        // Post-process rows
        const processedRows = await Promise.all(
            rows?.map(async (row) => {
                const year = moment(row._d).year();
                const month = moment(row._d).month() + 1; // moment: 0-based
                const workingDays = await getWorkingDaysInMonth(year, month);
                row.workingDays = workingDays;

                if (row.fixed_payroll && row.fixed_payroll > 0) {
                    try {
                        const effortsDetails = await getEffortsDetails(row);
                        if (effortsDetails) {
                            row.effortsDetails = effortsDetails;
                            row.workingDays = effortsDetails && effortsDetails[0]?.working_days;
                        }
                    } catch (error) {
                        logger.error("getFinanceReportDataByMonth failed", {}, error);
                    }

                    row.fixed_payroll = Number(row.fixed_payroll);
                    row.fixed_payment = Number(row.fixed_payment);

                    row.bill = (Number(row.fixed_payment) / Number(row.workingDays)) * Number(row._punched);
                    row.diff = (Number(row.fixed_diff) / Number(row.workingDays)) * Number(row._punched);
                    row.billPerDay = Number(row.fixed_payment) / Number(row.workingDays);

                    if (row.payment > 0) {
                        row.diff = 0;
                        row.payment = row.bill;
                    }
                } else {
                    row.billPerDay = null;
                }

                row.payment = Number(Number(row.payment).toFixed(2));
                row.bill = Number(Number(row.bill).toFixed(2));
                row.diff = Number(Number(row.diff).toFixed(2));

                // finance.updated_by is now included in row

                return row;
            }),
        );

        console.log("Processed Rows:", processedRows);

        return processedRows;
    } catch (error) {
        logger.error("getFinanceReportDataByMonth failed", {}, error);
        return [];
    }
}
export async function getOutstandingPayments(filters: any) {
    // console.log(filters);
    try {
        let _q =
            "SELECT  SUM(IFNULL(finance.payment, 0)) as payment, MONTHNAME(_d)  as month1, YEAR(_d) AS year, DATE_FORMAT(_d, '%Y-%m') AS month,sum( project.rate * finance.hrs) as bill, sum(hrs) as hrs,(COALESCE(sum(project.rate * finance.hrs),0)-COALESCE(sum(finance.payment),0)) as diff, (COALESCE((fixed_payroll),0)-COALESCE(sum(finance.payment),0)) as fixed_diff, finance.uuid ,project_master.name,consultant.name,project.rate from finance LEFT JOIN consultant ON consultant.uuid =finance.uuid LEFT JOIN project ON consultant.uuid = project.uuid AND _d BETWEEN project.start_dt AND project.end_dt  LEFT JOIN project_master ON project.pid = project_master.pid ";
        _q += " where 1=1 ";

        if (filters && filters.checkDate) {
            _q += "  and year(_d) = " + filters.checkDate.getFullYear() + "";
        }

        // _q += " group by month1 ";

        _q += " order by _d ";

        // console.log(_q);

        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("getOutstandingPayments failed", {}, error);
        return [];
    }
}

// export async function getfinances(request: any) {
//     let query = "";
//     if (this.filteredConsultant) {
//         query = "_d = " + '"' + request + '" and uid = ' + this.filteredConsultant.id;
//     } else if (this.filteredProject) {
//         query = "_d = " + '"' + request + '" and pid = ' + this.filteredProject.project;
//     } else {
//         query = "_d = " + '"' + request + '"';
//     }
//     this.financeService.getFinances(query).subscribe((result: any) => {
//         this.isdateeventclicked = true;
//         this.dialogHeader = "Payment Details";
//         this.finances = result;
//     });
// }

export async function saveFinanceRecord(financeObj: any) {
    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/api/finance", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(financeObj),
        });
        return await response.json();
    } catch (error) {
        logger.error("saveFinanceRecord failed", {}, error);
        return [];
    }
}

// function is not used
export async function getBillingDetails(filters: any) {
    // console.log(filters);

    let _q = "select distinct uuid, _d, month(_d) as _m, sum(pro) as _bill, sum(payment) as _payment, sum(hrs) as _hrs  from finance as f left join project as p on f.uuid = p.uuid where year(_d) = " + filters.checkDate.getFullYear() + " and month(_d) = " + (filters.checkDate.getMonth() + 1) + "  ";
    if (filters.employee) {
        _q += " and uuid = '" + filters.employee + "' ";
    } else {
        _q += " and uuid is not null and uuid <> '' ";
    }
    _q += " group by month(_d), uuid  ";
    // console.log(_q);

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("getBillingDetails failed", {}, error);
        return [];
    }
}

export async function adjustRate(params: AdjustRateParams) {
    // console.log(params);

    let _q = "UPDATE finance SET rate = " + Number(params.newRate) + " where 1=1 ";

    if (params.uuid) {
        _q += " and uuid = '" + params.uuid + "' ";
    }

    if (params.checkDate) {
        _q += " and month(_d) = " + (params.checkDate.getMonth() + 1) + " and year(_d) = " + params.checkDate.getFullYear();
    }

    console.log(_q);

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("adjustRate failed", {}, error);
        return [];
    }
}

export async function generateCustomInvoice(params: InvoiceRequestParams) {
    console.log(params);

    // Build SET clause dynamically
    let setClauses = [`invoice_no = '${params.invoiceNo}'`, `_ts_invoice = '${moment().format("YYYY-MM-DD")}'`, `_ts_invoice_due = DATE_ADD('${moment().format("YYYY-MM-DD")}', INTERVAL IFNULL(project_master.payment_terms, 30) DAY)`];

    if (params.createdBy) {
        setClauses.push(`finance.created_by = '${params.createdBy}'`);
    }
    if (params.updatedBy) {
        setClauses.push(`finance.updated_by = '${params.updatedBy}'`);
    }

    setClauses.push(`finance.operation = 'generate_invoice'`);
    setClauses.push(`finance.source = 'admin'`);

    // The due date is calculated as current date + payment_terms (in days) from the project_master table
    let updateQuery = `
        UPDATE finance
        LEFT JOIN project ON finance.pid = project.pid AND finance._d BETWEEN project.start_dt AND project.end_dt
        LEFT JOIN project_master ON project.pid = project_master.pid
        SET 
            ${setClauses.join(",\n            ")}
        WHERE 1=1
    `;

    if (params.uuid) {
        updateQuery += ` AND finance.uuid = '${params.uuid}' `;
    }

    if (params.checkDate) {
        updateQuery += ` AND month(_d) = ${params.checkDate.getMonth() + 1} AND year(_d) = ${params.checkDate.getFullYear()}`;
    }

    if (params.rate) {
        updateQuery += ` AND project.rate = ${params.rate} `;
    }
    console.log("updateQuery:", updateQuery);

    // Build a SELECT query to fetch updated rows
    let selectQuery = `
        SELECT 
            finance.uuid, 
            invoice_no, 
            _ts_invoice, 
            _ts_invoice_due, 
            SUM(project.rate * finance.hrs) AS bill, 
            SUM(hrs) AS hrs, 
            SUM(IFNULL(finance.payment, 0)) AS payment,
            finance.created_by,
            finance.updated_by
        FROM finance
        LEFT JOIN project ON finance.pid = project.pid AND finance._d BETWEEN project.start_dt AND project.end_dt
        LEFT JOIN project_master ON project.pid = project_master.pid
        WHERE 1=1
    `;

    if (params.uuid) {
        selectQuery += ` AND finance.uuid = '${params.uuid}' `;
    }

    if (params.checkDate) {
        selectQuery += ` AND month(_d) = ${params.checkDate.getMonth() + 1} AND year(_d) = ${params.checkDate.getFullYear()}`;
    }

    if (params.rate) {
        selectQuery += ` AND project.rate = ${params.rate} `;
    }

    selectQuery += ` GROUP BY finance.uuid, invoice_no, _ts_invoice, _ts_invoice_due`;

    console.log("selectQuery:", selectQuery);

    try {
        // First, run the UPDATE
        await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: updateQuery,
                params: ["finance"],
            }),
        });

        // Then, fetch the updated rows
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: selectQuery,
                params: ["finance"],
            }),
        });

        // Return the fetched data
        return await response.json();
    } catch (error) {
        logger.error("generateCustomInvoice failed", {}, error);
        return [];
    }
}

export async function generateInvoice(params: InvoiceRequestParams) {
    console.log(params);

    console.log(params.checkDate);

    // Build SET clause dynamically
    let setClauses = [`invoice_no = '${params.invoiceNo}'`, `_ts_invoice = '${moment().format("YYYY-MM-DD")}'`, `_ts_invoice_due = DATE_ADD('${moment().format("YYYY-MM-DD")}', INTERVAL IFNULL(project_master.payment_terms, 30) DAY)`];

    if (params.createdBy) {
        setClauses.push(`finance.created_by = '${params.createdBy}'`);
    }
    if (params.updatedBy) {
        setClauses.push(`finance.updated_by = '${params.updatedBy}'`);
    }

    setClauses.push(`finance.operation = 'generate_invoice'`);
    setClauses.push(`finance.source = 'admin'`);

    // The due date is calculated as current date + payment_terms (in days) from the project_master table
    let updateQuery = `
        UPDATE finance
        LEFT JOIN project ON finance.pid = project.pid AND finance._d BETWEEN project.start_dt AND project.end_dt
        LEFT JOIN project_master ON project.pid = project_master.pid
        SET 
            ${setClauses.join(",\n            ")}
        WHERE 1=1
    `;

    if (params.uuid) {
        updateQuery += ` AND finance.uuid = '${params.uuid}' `;
    }

    if (params.checkDate) {
        updateQuery += ` AND month(_d) = ${params.checkDate.getMonth() + 1} AND year(_d) = ${params.checkDate.getFullYear()}`;
    }

    if (params.rate) {
        updateQuery += ` AND project.rate = ${params.rate} `;
    }
    console.log("updateQuery:", updateQuery);

    // Build a SELECT query to fetch updated rows
    let selectQuery = `
        SELECT 
            finance.uuid, 
            invoice_no, 
            _ts_invoice, 
            _ts_invoice_due, 
            SUM(project.rate * finance.hrs) AS bill, 
            SUM(hrs) AS hrs, 
            SUM(IFNULL(finance.payment, 0)) AS payment,
            finance.created_by,
            finance.updated_by
        FROM finance
        LEFT JOIN project ON finance.pid = project.pid AND finance._d BETWEEN project.start_dt AND project.end_dt
        LEFT JOIN project_master ON project.pid = project_master.pid
        WHERE 1=1
    `;

    if (params.uuid) {
        selectQuery += ` AND finance.uuid = '${params.uuid}' `;
    }

    if (params.checkDate) {
        selectQuery += ` AND month(_d) = ${params.checkDate.getMonth() + 1} AND year(_d) = ${params.checkDate.getFullYear()}`;
    }

    if (params.rate) {
        selectQuery += ` AND project.rate = ${params.rate} `;
    }

    selectQuery += ` GROUP BY finance.uuid, invoice_no, _ts_invoice, _ts_invoice_due`;

    console.log("selectQuery:", selectQuery);

    try {
        // First, run the UPDATE
        await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: updateQuery,
                params: ["finance"],
            }),
        });

        // Then, fetch the updated rows
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: selectQuery,
                params: ["finance"],
            }),
        });

        // Return the fetched data
        return await response.json();
    } catch (error) {
        logger.error("generateInvoice failed", {}, error);
        return [];
    }
}

export async function generateWeeklyInvoice(params: InvoiceRequestParams) {
    console.log(params);

    let _q = "UPDATE finance SET `invoice_no` = CONCAT('" + params.invoiceNo + "-', WEEK(_d, 1)) , _ts_invoice = '" + moment(new Date()).add(0, "M").format("YYYY-MM-DD") + "', _ts_invoice_due = '" + moment(new Date()).add(1, "M").format("YYYY-MM-DD") + "', `operation` = 'generate_invoice', `source` = 'admin' where 1=1 ";

    if (params.uuid) {
        _q += " and uuid = '" + params.uuid + "' ";
    }

    if (params.checkDate) {
        // _q += " and month(_d) = " + (params.checkDate.getMonth() + 1); FIXME No month grouping for weekly invoices
        _q += " and year(_d) = " + params.checkDate.getFullYear();
    }

    if (params.week) {
        _q += " and WEEK(_d, 1) = " + params.week;
    }

    // _q += " group by WEEK(_d)";

    console.log(_q);

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("generateWeeklyInvoice failed", {}, error);
        return [];
    }
}

export async function generateCustomizedInvoice(params: InvoiceRequestParams) {
    console.log(params);
    //, DATE_FORMAT(pp.record_st_dt, '%Y%m%d'), '_', DATE_FORMAT(pp.record_end_dt, '%Y%m%d')

    // Build the base UPDATE with JOIN to project_period
    let _q = `UPDATE finance 
        LEFT JOIN project ON finance.pid = project.pid AND finance._d BETWEEN project.start_dt AND project.end_dt
        LEFT JOIN project_master ON project.pid = project_master.pid
        LEFT JOIN project_period pp ON finance.uuid = pp.uuid AND finance.pid = pp.pid AND finance._d BETWEEN pp.record_st_dt AND pp.record_end_dt
        SET 
            invoice_no = CONCAT('${params.invoiceNo}'),
            _ts_invoice = '${moment(new Date()).add(0, "M").format("YYYY-MM-DD")}',
            _ts_invoice_due = '${moment(new Date()).add(1, "M").format("YYYY-MM-DD")}',
            operation = 'generate_invoice',
            source = 'admin'
        WHERE 1=1`;

    if (params.uuid) {
        _q += ` AND finance.uuid = '${params.uuid}'`;
    }

    // If record_st_dt and record_end_dt are provided, use them for date range filtering
    if (params.record_st_dt && params.record_end_dt) {
        _q += ` AND finance._d >= '${params.record_st_dt}' AND finance._d <= '${params.record_end_dt}'`;
    }

    if (params.checkDate) {
        // Fallback to year filter if checkDate is provided
        _q += ` AND YEAR(finance._d) = ${params.checkDate.getFullYear()}`;
    }

    // Optionally add rate filter if present
    if (params.rate) {
        _q += ` AND project.rate = ${params.rate}`;
    }

    console.log(_q);

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("generateCustomizedInvoice failed", {}, error);
        return [];
    }
}

export async function markVerified(params: InvoiceRequestParams) {
    // console.log(params);

    // Build SET clause dynamically so we can optionally include created_by/updated_by
    const setClauses: string[] = [];
    setClauses.push(`verified = ${params.verified}`);
    setClauses.push(`_ts_verify = '${moment().format("YYYY-MM-DD")}'`);
    setClauses.push("operation = 'invoice_verify'");
    setClauses.push("source = 'admin'");

    if (params.updatedBy) {
        setClauses.push(`finance.updated_by = '${params.updatedBy}'`);
    }
    if (params.createdBy) {
        setClauses.push(`finance.created_by = '${params.createdBy}'`);
    }

    let _q = `UPDATE finance SET ${setClauses.join(", ")} where 1=1 `;

    if (params.uuid) {
        _q += " and uuid = '" + params.uuid + "' ";
    }

    if (params.checkDate) {
        _q += " and month(_d) = " + (params.checkDate.getMonth() + 1) + " and year(_d) = " + params.checkDate.getFullYear();
    }

    // console.log(_q);

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("markVerified failed", {}, error);
        return [];
    }
}

export async function performPaymentAPI(params: InvoiceRequestParams) {
    console.log(params);

    // Build SQL expression for payment with consistent rounding.
    // Priority:
    // 1) Explicit params.payment (rounded)
    // 2) If project.fixed_payment > 0 -> per-day fixed payment = fixed_payment / working_days (rounded),
    //    plus the sales tax configured for that employee/month.
    // 3) Otherwise -> (rate * hrs) after deductions (rounded)
    let paymentExpr: string;
    if (params?.payment !== undefined && params?.payment !== null && params.payment > 0) {
        paymentExpr = `ROUND(${params.payment}, 2)`;
    } else {
        // Use a CASE expression so each finance row uses fixed_payment logic when present,
        // otherwise falls back to rate*hrs with (insurance_fee + vmo) deductions.
        // The subselects fetch working_days and sales_tax for the finance row's month (format 'YYYY-MM').
        paymentExpr = `
            CASE
                WHEN COALESCE(project.fixed_payment, 0) > 0 THEN
                    ROUND(
                        COALESCE(project.fixed_payment, 0)
                        / NULLIF((
                            SELECT wd.working_days
                            FROM working_days wd
                            WHERE wd.uuid = finance.uuid AND wd.month_year = DATE_FORMAT(finance._d, '%Y-%m')
                            LIMIT 1
                        ), 0)
                        * (1 + COALESCE((
                            SELECT wd.sales_tax
                            FROM working_days wd
                            WHERE wd.uuid = finance.uuid AND wd.month_year = DATE_FORMAT(finance._d, '%Y-%m')
                            LIMIT 1
                        ), 0) / 100)
                    , 2)
                ELSE
                    ROUND(
                        ROUND( (COALESCE(project.rate, ${params.rate}) * hrs), 2)
                        * (1 - (COALESCE(project.insurance_fee, 0) + COALESCE(project.vmo, 0)) / 100)
                    , 2)
            END
        `;
    }

    // Build SET clause dynamically to optionally include created_by/updated_by
    const setClauses: string[] = [];
    setClauses.push(`payment = ${paymentExpr}`);
    setClauses.push(`_ts_payment = '${moment().format("YYYY-MM-DD")}'`);
    setClauses.push(`finance.operation = 'invoice_paid'`);
    setClauses.push(`finance.source = 'admin'`);

    if (params.updatedBy) {
        setClauses.push(`finance.updated_by = '${params.updatedBy}'`);
    }
    if (params.createdBy) {
        setClauses.push(`finance.created_by = '${params.createdBy}'`);
    }

    let _q = `UPDATE finance LEFT JOIN project ON finance.uuid = project.uuid
                AND finance.pid = project.pid
                AND finance._d BETWEEN project.start_dt AND project.end_dt
                SET ${setClauses.join(", ")}
                WHERE 1=1 `;

    if (params.uuid) {
        _q += ` AND finance.uuid = '${params.uuid}' `;
    }

    if (params.checkDate) {
        _q += ` AND year(finance._d) = ${params.checkDate.getFullYear()} `;
        if (params.billingPeriod !== "Weekly") {
            _q += ` AND month(finance._d) = ${params.checkDate.getMonth() + 1} `;
        }
    }

    if (params.record_st_dt && params.record_end_dt) {
        _q += ` AND finance._d BETWEEN '${params.record_st_dt}' AND '${params.record_end_dt}' `;
    }

    if (params.invoiceNo) {
        _q += ` AND finance.invoice_no = '${params.invoiceNo}' `;
    }

    if (params.week && params.billingPeriod === "Weekly") {
        _q += ` AND WEEK(finance._d, 1) = ${params.week} `;
    }

    console.log(_q);

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("performPaymentAPI failed", {}, error);
        return [];
    }
}

/**
 * Reset invoice-related fields for finance rows matching invoice_no and/or uuid.
 * Clears invoice_no, timestamps, payment and verification fields and audit fields.
 */
export async function resetInvoice(params: InvoiceRequestParams) {
    console.log("resetInvoice params:", params);

    let _q = `UPDATE finance SET 
        payment = NULL,
        verified = NULL,
        created_by = '',
        updated_by = '',
        _ts_bill = NULL,
        _ts_payment = NULL,
        _ts_verify = NULL,
        invoice_no = NULL,
        _ts_invoice = NULL,
        _ts_invoice_due = NULL
        WHERE 1=1`;

    if (params.invoiceNo) {
        // match exact invoice number
        _q += ` AND invoice_no = '${params.invoiceNo}'`;
    }

    if (params.uuid) {
        _q += ` AND uuid = '${params.uuid}'`;
    }

    // Optional: restrict by record range if provided
    if (params.record_st_dt && params.record_end_dt) {
        _q += ` AND _d BETWEEN '${params.record_st_dt}' AND '${params.record_end_dt}'`;
    }

    console.log("resetInvoice query:", _q);

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("resetInvoice failed", {}, error);
        return [];
    }
}

export async function getMonthlyInvoice(params: InvoiceRequestParams) {
    console.log(params);

    // If invoiceNo is provided, get details for that specific invoice
    if (params.invoiceNo) {
        let _q = `
        SELECT  
            f.invoice_no, 
            f._d, 
            p.rate, 
            SUM(CASE WHEN COALESCE(p.over_time, 0) = 1 THEN LEAST(IFNULL(f.hrs, 0), 8) ELSE IFNULL(f.hrs, 0) END * p.rate) as _sbill, 
            SUM(f.payment) as _spayment, 
            SUM(CASE WHEN COALESCE(p.over_time, 0) = 1 THEN LEAST(IFNULL(f.hrs, 0), 8) ELSE IFNULL(f.hrs, 0) END) as _shrs,
            MAX(p.over_time) as over_time,
            MAX(p.over_time_rate) as over_time_rate,
            SUM(GREATEST(0, f.hrs - 8)) as overtime_hours,
            COUNT(DISTINCT f._d) AS _punched,
            f._ts_invoice,
            MIN(f._ts_invoice) as _ts_invoice_label,
            f._ts_invoice_due,
            consultant.fixed_payroll,
            p.fixed_payment,
            pm.name as project_name,
            f.pid as project_pid,
            cm.name as client_name,
            cm.address as client_address,
            pm.vendor as vendor
        FROM finance as f 
        LEFT JOIN consultant ON consultant.uuid = f.uuid 
        LEFT JOIN project as p ON f.uuid = p.uuid AND f.pid = p.pid AND f._d BETWEEN p.start_dt AND p.end_dt
        LEFT JOIN project_master pm ON f.pid = pm.pid
        LEFT JOIN client_master cm ON pm.cid = cm.id
        WHERE f.invoice_no = '${params.invoiceNo}' AND f.hrs > 0
        `;

        if (params.uuid) {
            _q += " AND f.uuid = '" + params.uuid + "' ";
        }

        _q += " GROUP BY f.invoice_no, f.pid, pm.name, cm.name, cm.address, pm.vendor";

        console.log(_q);

        try {
            const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
                method: "post",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    query: _q,
                    params: ["finance"],
                }),
            });

            const rows = await response.json();

            // Post-process rows for fixed payment
            const processedRows = await Promise.all(
                rows.map(async (row) => {
                    const year = moment(row._d).year();
                    const month = moment(row._d).month() + 1;

                    let workingDays = await getWorkingDaysInMonth(year, month);
                    row.workingDays = workingDays;

                    if (Number(row.fixed_payment) > 0) {
                        try {
                            const effortsDetails = await getEffortsDetails({
                                _d: moment(row._d)
                                    .set("year", Number(year))
                                    .set("month", Number(month) - 1),
                                uuid: params?.uuid,
                            });

                            if (effortsDetails) {
                                row.effortsDetails = effortsDetails;
                                row.workingDays = effortsDetails[0]?.working_days;
                                row.sales_tax = effortsDetails[0]?.sales_tax;
                                workingDays = effortsDetails[0]?.working_days;
                            }
                        } catch (error) {
                            logger.error("getMonthlyInvoice failed", {}, error);
                        }

                        row._sbill = (Number(row.fixed_payment) / Number(workingDays)) * Number(row._punched);
                        row.billPerDay = Number(row.fixed_payment) / Number(workingDays);
                    }

                    return row;
                })
            );

            return processedRows;
        } catch (error) {
            logger.error("getMonthlyInvoice failed", {}, error);
            return [];
        }
    }

    // Otherwise, use the original logic for monthly invoice generation
    let _q = `
    SELECT  
        f.invoice_no, 
        f._ts_invoice, 
        MIN(f._ts_invoice) as _ts_invoice_label, 
        f._d, 
        COUNT(DISTINCT f._d) AS _punched,  
        f._ts_invoice_due, 
        p.rate, 
        SUM(CASE WHEN COALESCE(p.over_time, 0) = 1 THEN LEAST(IFNULL(f.hrs, 0), 8) ELSE IFNULL(f.hrs, 0) END * p.rate) as _sbill, 
        SUM(f.payment) as _spayment, 
        SUM(CASE WHEN COALESCE(p.over_time, 0) = 1 THEN LEAST(IFNULL(f.hrs, 0), 8) ELSE IFNULL(f.hrs, 0) END) as _shrs,
        MAX(p.over_time) as over_time,
        MAX(p.over_time_rate) as over_time_rate,
        SUM(GREATEST(0, f.hrs - 8)) as overtime_hours,
        consultant.fixed_payroll,
        p.fixed_payment
    FROM finance as f 
    LEFT JOIN consultant ON consultant.uuid = f.uuid 
    LEFT JOIN project as p ON f.uuid = p.uuid AND f.pid = p.pid AND f._d BETWEEN p.start_dt AND p.end_dt
    WHERE f.hrs > 0 
    `;

    if (params.uuid) {
        _q += " AND f.uuid = '" + params.uuid + "' ";
    }

    if (params.checkDate) {
        _q += " AND MONTH(f._d) = " + (params.checkDate.getMonth() + 1) + " AND YEAR(f._d) = " + params.checkDate.getFullYear();
    }

    _q += " GROUP BY MONTH(f._d) HAVING _punched > 0";
    _q += " ORDER BY f._d DESC, f.invoice_no DESC";

    console.log(_q);

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });

        const rows = await response.json(); // your DB fetch

        // console.log("Rows fetched:", rows);

        // Post-process rows
        const processedRows = await Promise.all(
            rows.map(async (row) => {
                console.log("Processing row:", row);

                const year = moment(row._d).year();
                const month = moment(row._d).month() + 1; // moment: 0-based

                // Working Days Calculation
                let workingDays = await getWorkingDaysInMonth(year, month);
                row.workingDays = workingDays;

                if (Number(row.fixed_payment) > 0) {
                    try {
                        const effortsDetails = await getEffortsDetails({
                            _d: moment(row?._d)
                                .set("year", Number(year))
                                .set("month", Number(month) - 1),
                            uuid: params?.uuid,
                        });
                        console.error(effortsDetails);

                        if (effortsDetails) {
                            // && effortsDetails.length > 0 // FIXME
                            // console.log("Efforts details for row:", row, "is", effortsDetails);
                            // Any logic that depends on effortsDetails should go here

                            row.effortsDetails = effortsDetails;
                            row.workingDays = effortsDetails && effortsDetails[0]?.working_days; // Use fetched working days or fallback to calculated
                            row.sales_tax = effortsDetails[0]?.sales_tax;

                            workingDays = effortsDetails && effortsDetails[0]?.working_days; // Use fetched working days or fallback to calculated
                        }

                        // console.log(row);
                    } catch (error) {
                        logger.error("getMonthlyInvoice failed", {}, error);
                    }

                    row._sbill = (Number(row.fixed_payment) / Number(workingDays)) * Number(row._punched);
                    row.diff = (Number(row.fixed_diff) / Number(workingDays)) * Number(row._punched);
                    row.billPerDay = Number(row.fixed_payment) / Number(workingDays);
                }

                console.log("Row after processing:", row);

                return {
                    ...row,
                };
            }),
        );

        return processedRows;
    } catch (error) {
        logger.error("getMonthlyInvoice failed", {}, error);
        return [];
    }
}

export async function getMonthlyItemizedInvoice(params: InvoiceRequestParams) {
    console.log(params);

    const startOfMonth = moment(params.checkDate).startOf("month").format("YYYY-MM-DD");
    const endOfMonth = moment(params.checkDate).endOf("month").format("YYYY-MM-DD");

    let _q = "with recursive all_dates(dt) as (select '" + startOfMonth + "' dt union all select dt + interval 1 day from all_dates where dt < '" + endOfMonth + "') ";

    _q +=
        "  SELECT p.vmo as vmo, p.insurance_fee as insurance_fee, invoice_no, _ts_invoice, min(_ts_invoice) as _ts_invoice_label, _d,  _ts_invoice_due,  p.rate, max(p.rate) as maxrate, MIN(cd.dt) as first_day_of_week, MAX(cd.dt) as last_day_of_week, WEEK(_d, 5) _date, count(_d) as _count, sum(CASE WHEN COALESCE(p.over_time, 0) = 1 THEN LEAST(IFNULL(ft.hrs, 0), 8) ELSE IFNULL(ft.hrs, 0) END * p.rate) as _sbill, sum(payment) as _spayment, sum(paid) as _spaid, sum(verified), COALESCE(sum(CASE WHEN COALESCE(p.over_time, 0) = 1 THEN LEAST(IFNULL(ft.hrs, 0), 8) ELSE IFNULL(ft.hrs, 0) END), 0) as _shrs, MAX(p.over_time) as over_time, MAX(p.over_time_rate) as over_time_rate, COALESCE(sum(GREATEST(0, ft.hrs - 8)), 0) as overtime_hours, pm.name as project_name, p.pid as project_pid, pm.vendor as vendor, cm.name as client_name, cm.address as client_address from all_dates cd left join finance ft on ft._d = cd.dt and uuid = '" +
        params.uuid +
        "' AND month(_d) = " +
        (params.checkDate.getMonth() + 1) +
        " and year(_d) = " +
        params?.checkDate?.getFullYear() +
        " left join project p on ft.uuid= p.uuid and dt between p.start_dt and p.end_dt left join project_master pm on pm.pid = p.pid left join client_master cm on pm.cid = cm.id group by WEEK(cd.dt, 5)";

    console.log(_q);

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("getMonthlyItemizedInvoice failed", {}, error);
        return [];
    }
}

export async function getCustomisedInvoice(params: InvoiceRequestParams) {
    console.log(params);
    let _q = `
        SELECT 
            p.vmo as vmo,
            p.insurance_fee as insurance_fee,
            f.invoice_no,
            f._ts_invoice,
            MIN(f._ts_invoice) as _ts_invoice_label,
            COALESCE(pp.record_st_dt, MIN(f._d)) as period_start,
            COALESCE(pp.record_end_dt, MAX(f._d)) as period_end,
            f._ts_invoice_due,
            p.rate,
            MAX(p.rate) as maxrate,
            COALESCE(LEAST(MIN(f._d), MIN(pp.record_st_dt)), MIN(f._d)) as first_day_of_period,
            COALESCE(GREATEST(MAX(f._d), MAX(pp.record_end_dt)), MAX(f._d)) as last_day_of_period,
            COUNT(f._d) as _count,
            ROUND(SUM(CASE WHEN COALESCE(p.over_time, 0) = 1 THEN LEAST(IFNULL(f.hrs, 0), 8) ELSE IFNULL(f.hrs, 0) END * p.rate), 2) as _sbill,
            ROUND(SUM(CASE WHEN COALESCE(p.over_time, 0) = 1 THEN LEAST(IFNULL(f.hrs, 0), 8) ELSE IFNULL(f.hrs, 0) END * p.rate) * (1 - (COALESCE(p.insurance_fee,0) + COALESCE(p.vmo,0)) / 100), 2) as _sbill_with_deductions,
            SUM(f.payment) as _spayment,
            SUM(f.paid) as _spaid,
            SUM(f.verified) as verified,
            COALESCE(SUM(CASE WHEN COALESCE(p.over_time, 0) = 1 THEN LEAST(IFNULL(f.hrs, 0), 8) ELSE IFNULL(f.hrs, 0) END), 0) as _shrs,
            MAX(p.over_time) as over_time,
            MAX(p.over_time_rate) as over_time_rate,
            COALESCE(SUM(GREATEST(0, f.hrs - 8)), 0) as overtime_hours,
            pm.name as project_name,
            p.pid as project_pid,
            pm.vendor as vendor,
            cm.name as client_name,
            cm.address as client_address
        FROM finance f
        LEFT JOIN project_period pp ON 
            f.uuid = pp.uuid 
            AND f.pid = pp.pid 
            AND f._d BETWEEN pp.record_st_dt AND pp.record_end_dt
            AND pp.id = (
                SELECT pp2.id
                FROM project_period pp2
                WHERE pp2.uuid = f.uuid
                    AND pp2.pid = f.pid
                    AND f._d BETWEEN pp2.record_st_dt AND pp2.record_end_dt
                ORDER BY pp2.record_st_dt DESC, pp2.id ASC
                LIMIT 1
            )
        LEFT JOIN project p ON f.uuid = p.uuid AND f.pid = p.pid AND f._d BETWEEN p.start_dt AND p.end_dt
        LEFT JOIN project_master pm ON pm.pid = p.pid
        LEFT JOIN client_master cm ON pm.cid = cm.id
        WHERE f.uuid = '${params.uuid}'
          AND f.invoice_no = '${params.invoiceNo}'
        GROUP BY f.uuid, f.pid, pp.record_st_dt, pp.record_end_dt
        ORDER BY pp.record_st_dt, pp.record_end_dt, f._d
    `;

    //ft.invoice_no, ft._ts_invoice, ft._ts_invoice_due, p.vmo, p.insurance_fee, p.rate, p.pid

    console.log(_q);

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("getCustomisedInvoice failed", {}, error);
        return [];
    }
}

// method not used
export async function getMonthlyItemizedInvoiceOld(params: InvoiceRequestParams) {
    console.log(params);

    let _q = "SELECT invoice_no, _ts_invoice, _d,  _ts_invoice_due,  rate, MIN(_d) as first_day_of_week, MAX(_d) as last_day_of_week, WEEK(_d) _date, count(_d) as _count, sum(bill) as _sbill, sum(payment) as _spayment, sum(paid) as _spaid, sum(verified), sum(hrs) as _shrs from finance where uuid = '" + params.uuid + "' AND month(_d) = " + (params.checkDate.getMonth() + 1) + " and year(_d) = " + params.checkDate.getFullYear() + " group by WEEK(_d)";
    console.log(_q);

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("getMonthlyItemizedInvoiceOld failed", {}, error);
        return [];
    }
}

export async function getWeeklyInvoice(params: InvoiceRequestParams) {
    console.log(params);

    let _q =
        "SELECT finance.invoice_no, finance._ts_invoice, min(finance._ts_invoice) as _ts_invoice_label, finance._d,  finance._ts_invoice_due,  project.rate, DATE_SUB(MIN(finance._d), INTERVAL WEEKDAY(MIN(finance._d)) DAY) as first_day_of_week, DATE_ADD(DATE_SUB(MIN(finance._d), INTERVAL WEEKDAY(MIN(finance._d)) DAY), INTERVAL 6 DAY) as last_day_of_week, WEEK(finance._d, 1) _date, count(finance._d) as _count, sum(CASE WHEN COALESCE(project.over_time, 0) = 1 THEN LEAST(IFNULL(finance.hrs, 0), 8) ELSE IFNULL(finance.hrs, 0) END * project.rate) as _sbill, sum(finance.payment) as _spayment, sum(finance.paid) as _spaid, sum(finance.verified), sum(CASE WHEN COALESCE(project.over_time, 0) = 1 THEN LEAST(IFNULL(finance.hrs, 0), 8) ELSE IFNULL(finance.hrs, 0) END) as _shrs, MAX(project.over_time) as over_time, MAX(project.over_time_rate) as over_time_rate, sum(GREATEST(0, finance.hrs - 8)) as overtime_hours, pm.name as project_name, project.pid as project_pid, pm.vendor as vendor, cm.name as client_name, cm.address as client_address from finance left join project on finance.uuid = project.uuid AND finance.pid = project.pid AND finance._d BETWEEN project.start_dt AND project.end_dt left join project_master pm on project.pid = pm.pid left join client_master cm on pm.cid = cm.id where  finance.hrs > 0 and finance.uuid = '" +
        params.uuid +
        "'";

    if (params.invoiceNo) {
        _q += " AND finance.invoice_no = '" + params.invoiceNo + "'";
    } else {
        _q += " AND month(finance._d) = " + (params.checkDate.getMonth() + 1) + " and year(finance._d) = " + params.checkDate.getFullYear() + " AND WEEK(finance._d, 1)=" + params.week + " AND finance.invoice_no = ''";
    }

    _q += " group by WEEK(finance._d, 1), finance.pid, pm.name, project.pid, pm.vendor, cm.name, cm.address";
    console.log(_q);

    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: _q,
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("getWeeklyInvoice failed", {}, error);
        return [];
    }
}
// // no need to change since finance contains pid
export async function getFinances(currMonth: any, curryear: any, uuid: any, pid: any, setIsLoading: any) {
    setIsLoading(true);
    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: "SELECT  hrs,DAY(_d) as day,WEEK(_d) as week, _d , uuid, pid, paid, rate, hrs, bill, payment, verified, name from finance where  MONTH(_d) = " + currMonth + " AND YEAR(_d) = " + curryear + " and uuid ='" + uuid + "' and pid = '" + pid + "'",
                // query: "SELECT  f.hrs,  DAY(f._d) AS day,  WEEK(f._d, 4) AS week, f._d,   f.uuid,  f.pid,   f.verified,  p.start_dt, p.end_dt FROM finance f JOIN project p ON f.pid = p.pid WHERE MONTH(f._d) =  " + currMonth + " AND YEAR(_d) = " + curryear + " and f.uuid ='" + uuid + "' and pid = '" + pid + "'",
                params: ["finance"],
            }),
        });
        return await response.json();
    } catch (error) {
        logger.error("getFinances failed", {}, error);
        return [];
    }
}

export async function getWorkingDaysData() {
    console.log("Fetching working days data...");
    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: "SELECT wd.uuid, wd.month_year, wd.working_days, wd.sales_tax, c.name as employee_name FROM working_days wd LEFT JOIN consultant c ON wd.uuid = c.uuid ORDER BY wd.month_year DESC, c.name, wd.uuid",
                params: ["working_days"],
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        logger.error("getWorkingDaysData failed", {}, error);
        return [];
    }
}

export async function updateWorkingDaysData(uuid: string, monthYear: string, workingDays: number) {
    console.log("Updating working days data...", { uuid, monthYear, workingDays });
    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: `UPDATE working_days SET working_days = ${workingDays} WHERE uuid = '${uuid}' AND month_year = '${monthYear}'`,
                params: ["working_days"],
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        logger.error("updateWorkingDaysData failed", {}, error);
        throw error;
    }
}

export async function updateWorkingDaysSalesTax(uuid: string, monthYear: string, salesTax: number) {
    console.log("Updating working days sales tax...", { uuid, monthYear, salesTax });
    try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/dynamic", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: salesTax > 0
                    ? `UPDATE working_days SET sales_tax = ${salesTax} WHERE uuid = '${uuid}' AND month_year = '${monthYear}'`
                    : `UPDATE working_days SET sales_tax = NULL WHERE uuid = '${uuid}' AND month_year = '${monthYear}'`,
                params: ["working_days"],
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        logger.error("updateWorkingDaysSalesTax failed", {}, error);
        throw error;
    }
}
